import { useRef } from 'react';
import { useReactFlow } from '@xyflow/react';
import { TipoAlvo, TipoNota, type AlvoDto } from '@nexital/shared';
import { useDependencias, useQuadro } from '../../application/dependencias';
import { posicaoSemTrocarAlvo, soltarPostIt, type AlvoNoQuadro } from '../../domain/notas';
import { MARGEM_LIXEIRA } from '../../domain/soltura';
import { useNotas } from '../notas/contexto-notas';
import type { NoNotaTipo } from '../notas/montar-nos-notas';
import type { Ferramenta } from './BarraFerramentas';
import { uuidDoNo, type NoCartaoTipo, type NoGrupoTipo } from './montar-nos';
import type { NoDoQuadro } from './use-nos-quadro';
import { pontoDoEvento, pontoSobre, useSoltura } from './use-soltura';

type EventoArraste = MouseEvent | TouchEvent;

/** ~20 mensagens por segundo no arraste ao vivo (ADR-0008). */
const INTERVALO_ARRASTE_MS = 50;

const alvoDoNo = (id: string): AlvoDto => {
  const tipo = { c: TipoAlvo.Processo, g: TipoAlvo.Grupo }[id[0] as 'c' | 'g'] ?? TipoAlvo.Nota;
  return { tipo, uuid: uuidDoNo(id) };
};

const geometria = ({ id, position, data }: NoGrupoTipo) => {
  const { largura, altura, rotacao } = data.grupo;
  return { uuid: uuidDoNo(id), x: position.x, y: position.y, largura, altura, rotacao };
};

/** O dono prende o post-it ao que estiver por baixo; os outros só o movem, no mesmo alvo (RQ-NOT-010). */
const mudancaAoSoltar = ({ position, data }: NoNotaTipo, alvos: AlvoNoQuadro[]) => {
  if (!data.nota.podeEditar) {
    const doAlvo = (alvo: AlvoDto) => alvos.find((a) => a.alvo.uuid === alvo.uuid) ?? null;
    return posicaoSemTrocarAlvo(position, data.nota.presoA, doAlvo);
  }
  const { presoA, posicao } = soltarPostIt(position, alvos);
  return { ...posicao, presoA };
};

type Opcoes = {
  ferramenta: Ferramenta;
  definirFerramenta: (f: Ferramenta) => void;
  lixeira: React.RefObject<HTMLElement | null>;
  abrirLixeira: (aberta: boolean) => void;
  voltar: (idNo: string) => void;
  alvos: AlvoNoQuadro[];
};

/**
 * Arrastar, soltar e ferramentas do quadro: trava leve ao começar (RQ-COL-004), arraste ao vivo (RQ-COL-002),
 * cartões e grupos como antes, post-its presos ao que estiver por baixo (RQ-NOT-003), post-it onde se clica e seta
 * do primeiro item clicado (origem) ao segundo (destino, onde fica a ponta) (RQ-NOT-004).
 */
export function useInteracaoQuadro(o: Opcoes) {
  const { canal, storeQuadro } = useDependencias();
  const alterarGrupos = useQuadro((s) => s.alterarGrupos);
  const { criarNota, alterarNota, criarSeta } = useNotas((s) => s);
  const { soltarCartao, receberDaBandeja } = useSoltura(o.voltar);
  const { screenToFlowPosition } = useReactFlow();
  const negados = useRef(new Set<string>());
  const ultimoEnvio = useRef(0);
  const origemSeta = useRef<string | null>(null);
  const sobreLixeira = (evento: EventoArraste) => pontoSobre(o.lixeira.current, pontoDoEvento(evento), MARGEM_LIXEIRA);

  const aoComecar = (_: unknown, __: unknown, nos: NoDoQuadro[]) => {
    storeQuadro.getState().definirEmArraste(true);
    negados.current.clear();
    for (const no of nos) void canal.travar(no.id).then((r) => r.ok || negados.current.add(no.id));
  };

  const aoArrastar = (evento: EventoArraste, no: NoDoQuadro, nos: NoDoQuadro[]) => {
    o.abrirLixeira(no.type === 'cartao' && sobreLixeira(evento));
    if (Date.now() - ultimoEnvio.current < INTERVALO_ARRASTE_MS) return;
    ultimoEnvio.current = Date.now();
    canal.arrastando(nos.map((n) => ({ id: n.id, x: n.position.x, y: n.position.y })));
  };

  const aoSoltar = (evento: EventoArraste, _: unknown, soltos: NoDoQuadro[]) => {
    for (const no of soltos) canal.liberar(no.id);
    storeQuadro.getState().definirEmArraste(false);
    // Outra pessoa já tinha a trava: o que foi arrastado volta e nada é gravado.
    if (soltos.some((n) => negados.current.has(n.id))) return soltos.forEach((n) => o.voltar(n.id));
    const cartoes = soltos.filter((n): n is NoCartaoTipo => n.type === 'cartao');
    const naLixeira = sobreLixeira(evento);
    void Promise.all(cartoes.map((c) => soltarCartao(c, naLixeira, cartoes.length > 1))).finally(() => o.abrirLixeira(false));
    const grupos = soltos.filter((n): n is NoGrupoTipo => n.type === 'grupo');
    if (grupos.length) void alterarGrupos(grupos.map(geometria));
    for (const nota of soltos.filter((n): n is NoNotaTipo => n.type === 'nota')) void alterarNota(uuidDoNo(nota.id), mudancaAoSoltar(nota, o.alvos));
  };

  /** Post-it onde se clicou; a ferramenta volta à seleção. */
  const aoClicarNoVazio = (evento: React.MouseEvent) => {
    if (o.ferramenta !== 'nota') return;
    const { x, y } = screenToFlowPosition({ x: evento.clientX, y: evento.clientY });
    void criarNota({ tipo: TipoNota.PostIt, texto: '', x, y });
    o.definirFerramenta('selecao');
  };

  /** Com o conector: o primeiro clique escolhe a origem, o segundo cria a seta até o destino. */
  const aoClicarNo = (_: React.MouseEvent, no: NoDoQuadro) => {
    if (o.ferramenta !== 'conector') return;
    const origem = origemSeta.current;
    if (!origem || origem === no.id) return void (origemSeta.current = no.id);
    origemSeta.current = null;
    void criarSeta({ origem: alvoDoNo(origem), destino: alvoDoNo(no.id) });
    o.definirFerramenta('selecao');
  };

  return { aoComecar, aoArrastar, aoSoltar, aoClicarNoVazio, aoClicarNo, receberDaBandeja, origemSeta };
}
